<template>
  <section id="products">
    <div
      v-if="title"
      class="row justify-center items-center q-gutter-md q-pb-md heading"
    >
      <div class="title">
        <h2 class="text-h4 text-weight-medium no-margin">
          {{ title }}
        </h2>
      </div>
    </div>

    <div
      ref="toolsRef"
      class="row flex-center justify-evenly q-gutter-sm q-mb-sm tools"
    >
      <q-select
        v-model="sort"
        :options="sorts"
        :disable="!total || status == 'pending'"
        :clearable="!!sort"
        label="Sort By"
        class="sort"
        hide-bottom-space
        filled
        square
        emit-value
        map-options
        dense
      >
        <template #after>
          <q-btn
            :icon="mdiFilter"
            :disable="!total || status == 'pending'"
            label="Filters"
            padding="sm"
            color="secondary"
            square
            flat
            @click="dialog({
              component: LazyDialogCatalogFilters,
              componentProps: {
                query,
              },
            })"
          >
            <lazy-q-badge
              v-if="active.length"
              :label="active.length"
              color="primary"
              floating
              rounded
            />
          </q-btn>
        </template>
      </q-select>
      <div>
        <h3 class="text-h6 text-weight-light no-margin">
          Found {{ pluralize(total, 'product') }}
        </h3>
      </div>
      <q-pagination
        v-model="page"
        :max="pages"
        :disable="!total || status == 'pending'"
        size="lg"
        direction-links
        flat
        input
      />
    </div>

    <lazy-q-banner
      v-if="status == 'success' && !items.length"
      class="text-white bg-red full-width q-my-md"
      inline-actions
    >
      No products found that match your request. You can retry your query or
      clear it.
      <template #action>
        <q-btn
          flat
          square
          color="white"
          label="Retry"
          @click="refresh()"
        />
        <q-btn
          flat
          square
          color="white"
          label="Reset"
          @click="reset()"
        />
      </template>
    </lazy-q-banner>

    <LazyCatalogGrid
      v-else
      :items
      :size
      :loading="status == 'pending'"
    />

    <div
      v-if="pages > 1"
      class="row flex-center q-mt-sm"
    >
      <q-pagination
        v-model="page"
        :max="pages"
        :disable="!total || status == 'pending'"
        size="lg"
        direction-links
        flat
        input
        @update:model-value="scrollToTop()"
      />
    </div>
  </section>
</template>

<script lang="ts" setup>
import { mdiFilter } from '@quasar/extras/mdi-v7';
import { scroll } from 'quasar';
import { LazyDialogCatalogFilters } from '#components';
import type { Product } from '~/types/search';
import type { FilterByQuery, SearchParams } from '~/types/typesense';

const { getScrollTarget, setVerticalScrollPosition } = scroll;

const props = defineProps({
  query: {
    type: Object as PropType<FilterByQuery<Product>>,
    default: () => ({}),
  },
  name: {
    type: String,
  },
  title: {
    type: String,
  },
  dataKey: {
    type: String,
    required: true,
  },
});

const { $typesense } = useNuxtApp();
const { buildFieldsList, buildFilterBy } = useTypesenseUtils();
const { dialog } = useQuasar();
const route = useRoute();
const toolsRef = ref<HTMLElement | null>(null);
const sorts = PRODUCT_SORTS;

const { search, sort, page, size, queries, active } = useFilters();
useProvideCollection(props.name ?? props.title);

const total = computed(() => data.value?.found ?? 0);

const items = computed(() => data.value?.hits?.map(h => h.document) ?? []);

const filterBy = computed(() => ({ ...props.query, $and: queries.value }));

const params = computed<SearchParams>(() => ({
  q: search.value?.length ? search.value : '*',
  query_by: buildFieldsList<Product>(PRODUCT_QUERY_BY),
  highlight_fields: 'none',
  include_fields: buildFieldsList<Product>(PRODUCT_INCLUDE_FIELDS),
  filter_by: buildFilterBy<Product>(filterBy.value),
  sort_by: sort.value,
  page: page.value,
  per_page: size.value,
  enable_analytics: !!search.value?.length,
}));

const { data, status, refresh } = useAsyncData(props.dataKey,
  () => $typesense.search<Product>('kygunco_products', params.value),
  {
    lazy: true,
  },
);

const { pluralize } = useFormatting();

const pages = computed(() => Math.ceil(total.value / size.value) || page.value);

const reset = () => navigateTo({ path: route.path });

const scrollToTop = () => {
  if (!toolsRef.value) {
    return;
  }

  setVerticalScrollPosition(
    getScrollTarget(toolsRef.value),
    toolsRef.value.offsetTop,
    500,
  );
};

const stopWatcher = watch(params, () => refresh());

onBeforeRouteLeave(stopWatcher);
</script>

<style lang="scss" scoped>
.tools {
  @media (min-width: $breakpoint-sm-min) {
    justify-content: space-between;
  }

  .sort {
    width: 100%;

    @media (min-width: $breakpoint-sm-min) {
      width: auto;
      min-width: 250px;
    }
  }
}
</style>
